import { createPageTabNavigation } from "../page-tab-navigation.js";
import { applyFishydexSurfaceProjection } from "../fishydex/surface-projection.js";

const GRADE_ORDER = Object.freeze(["red", "yellow", "blue", "green", "white"]);

const DEVICE_INTENT_FIELDS = Object.freeze([
  "search_query",
  "show_non_fish",
  "sort_field",
  "sort_direction",
  "catalog_view",
]);

function setVisible(element, visible) {
  if (element instanceof HTMLElement) element.hidden = !visible;
}

function setPressed(button, pressed, activeClass = "btn-active") {
  if (!(button instanceof HTMLElement)) return;
  button.classList.toggle(activeClass, pressed);
  button.setAttribute("aria-pressed", String(pressed));
}

function boundedSearch(value) {
  if (typeof value !== "string") throw new TypeError("Fishydex search query must be text");
  if (Array.from(value).length > 160 || /[\u0000-\u001f\u007f]/.test(value)) {
    throw new TypeError("Fishydex search query exceeds its bounded text contract");
  }
  return value;
}

function languageText(key, vars = {}) {
  const helper = globalThis.window?.__fishystuffLanguage;
  return typeof helper?.t === "function" ? helper.t(key, vars) : key;
}

function gradeLabel(grade) {
  const key = `domain.grade.${grade}`;
  const translated = languageText(key);
  return translated === key ? languageText("domain.grade.unknown") : translated;
}

function progressAriaLabel(kind, caught, total, percent) {
  return languageText(
    kind === "catalog" ? "fishydex.progress.aria.catalog" : "fishydex.progress.aria.grade",
    {
      ...(kind === "catalog" ? {} : { grade: gradeLabel(kind) }),
      caught,
      total,
      percent,
    },
  );
}

function remainingLabel(total, caught, emptyKey) {
  return total > 0
    ? languageText("fishydex.progress.remaining", { count: Math.max(total - caught, 0) })
    : languageText(emptyKey);
}

function matchesLabel(visible, total) {
  return languageText("fishydex.filter.matches", { visible, total });
}

function sortLabel(field, currentField, direction) {
  const fieldLabel = languageText(
    field === "price" ? "fishydex.catalog.sort.price" : "fishydex.catalog.sort.name",
  );
  return currentField === field
    ? languageText("fishydex.catalog.sort.current", {
      field: fieldLabel,
      direction: languageText(
        `fishydex.catalog.sort.direction.${direction === "desc" ? "desc" : "asc"}`,
      ),
    })
    : languageText("fishydex.catalog.sort.default", { field: fieldLabel });
}

export class FishydexSurfaceElement extends HTMLElement {
  #state = null;
  #activeTab = "catalog";
  #navigation = null;
  #abortController = null;

  connectedCallback() {
    if (this.#abortController) return;
    this.#abortController = new AbortController();
    const options = { signal: this.#abortController.signal };
    this.addEventListener("input", (event) => this.#onInput(event), options);
    this.addEventListener("compositionend", (event) => this.#onInput(event), options);
    this.addEventListener("click", (event) => this.#onClick(event), options);
    this.addEventListener("keydown", (event) => this.#onTabKeydown(event), options);
    this.#navigation = createPageTabNavigation({
      page: "dex",
      basePath: new URL(document.getElementById("page").dataset.fishydexBaseHref, window.location.href).pathname,
      root: this,
      project: (tab) => { this.#activeTab = tab; this.#renderTabs(); },
    });
    this.#navigation.start();
    this.dispatchEvent(new CustomEvent("fishydex-surface-connected", { bubbles: true }));
  }

  disconnectedCallback() {
    this.querySelector("#fishydex-display-options")?.hidePopover();
    this.removeAttribute("data-fishydex-preferences-ready");
    this.dispatchEvent(new CustomEvent("fishydex-surface-disconnected"));
    this.#navigation?.stop();
    this.#navigation = null;
    this.#abortController?.abort();
    this.#abortController = null;
  }

  applyProjection(patch) {
    if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
      throw new TypeError("Fishydex component projection must be an object");
    }
    this.#state = applyFishydexSurfaceProjection(this.#state, patch);
    this.render();
    if (!this.hasAttribute("data-fishydex-preferences-ready")) {
      this.setAttribute("data-fishydex-preferences-ready", "");
    }
  }

  #emitDeviceIntent(patch) {
    if (this.#state === null) return;
    const next = { ...this.#state, ...structuredClone(patch) };
    const detail = Object.fromEntries(
      DEVICE_INTENT_FIELDS.map((field) => [field, structuredClone(next[field])]),
    );
    this.dispatchEvent(new CustomEvent("fishydex-intent", {
      bubbles: true,
      detail: {
        kind: "replace-device-ui",
        preferences: detail,
      },
    }));
  }

  #emitShowDriedIntent(enabled) {
    this.dispatchEvent(new CustomEvent("fishydex-intent", {
      bubbles: true,
      detail: { kind: "set-show-dried", enabled },
    }));
  }

  #emitClearFiltersIntent() {
    if (this.#state === null) return;
    const input = this.querySelector(".fishydex-search");
    if (input) input.value = "";
    const next = {
      ...this.#state,
      search_query: "",
      show_non_fish: false,
    };
    const preferences = Object.fromEntries(
      DEVICE_INTENT_FIELDS.map((field) => [field, structuredClone(next[field])]),
    );
    this.dispatchEvent(new CustomEvent("fishydex-intent", {
      bubbles: true,
      detail: {
        kind: "clear-filters",
        preferences,
        showDried: false,
      },
    }));
  }

  #onInput(event) {
    if (event.isComposing) return;
    if (event.target instanceof HTMLInputElement && event.target.matches("[data-fishydex-show-non-fish]")) {
      this.#emitDeviceIntent({ show_non_fish: event.target.checked });
      return;
    }
    if (event.target instanceof HTMLInputElement && event.target.matches("[data-fishydex-show-dried]")) {
      this.#emitShowDriedIntent(event.target.checked);
      return;
    }
    if (event.target instanceof HTMLInputElement && event.target.matches(".fishydex-search")) {
      const query = boundedSearch(event.target.value);
      if (query.trim() !== this.#state?.search_query) this.#emitDeviceIntent({ search_query: query });
    }
  }

  #onClick(event) {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest("[data-fishydex-clear-filters]")) {
      this.#emitClearFiltersIntent();
      return;
    }
    const view = event.target.closest("[data-fishydex-catalog-view]");
    if (view instanceof HTMLElement) {
      this.#emitDeviceIntent({ catalog_view: view.dataset.fishydexCatalogView });
      return;
    }
    const sort = event.target.closest("[data-fishydex-sort]");
    if (sort instanceof HTMLElement) {
      const field = sort.dataset.fishydexSort;
      this.#emitDeviceIntent({
        sort_field: field,
        sort_direction: this.#state.sort_field === field
          ? this.#state.sort_direction === "desc" ? "asc" : "desc"
          : "asc",
      });
    }
  }

  #onTabKeydown(event) {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest("[data-fishydex-tab]");
    if (!tab || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    const tabs = [...this.querySelectorAll("[data-fishydex-tab]")];
    const index = tabs.indexOf(tab);
    const next = event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1
      : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
    event.preventDefault();
    tabs[next].click();
    tabs[next].focus();
  }

  #renderTabs() {
    if (this.#activeTab !== "catalog") this.querySelector("#fishydex-display-options")?.hidePopover();
    setVisible(this.querySelector("#fishydex-query"), this.#activeTab === "catalog");
    for (const tab of this.querySelectorAll("[data-fishydex-tab]")) {
      const selected = tab.dataset.fishydexTab === this.#activeTab;
      tab.classList.toggle("tab-active", selected);
      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }
    for (const panel of this.querySelectorAll("[data-fishydex-tab-panel]")) {
      panel.hidden = panel.dataset.fishydexTabPanel !== this.#activeTab;
    }
  }

  #renderProgress(kind) {
    const prefix = kind === "catalog" ? "" : `${kind}_`;
    const caught = this.#state[`${prefix}caught_count`];
    const total = kind === "catalog"
      ? this.#state.fish_count
      : this.#state[`${prefix}total_count`];
    const percent = this.#state[`${prefix}completion_percent`];
    const ring = kind === "catalog"
      ? this.querySelector("#fishydex-progress-body .fishydex-progress-pie.is-overall")
      : this.querySelector(`#fishydex-progress-body .fishydex-progress-pie.grade-${kind}`);
    if (!(ring instanceof HTMLElement)) return;
    ring.style.setProperty("--value", String(percent));
    ring.style.setProperty("--size", kind === "catalog" ? "7.5rem" : "4.85rem");
    ring.style.setProperty("--thickness", kind === "catalog" ? "0.72rem" : "0.48rem");
    ring.setAttribute("aria-valuenow", String(percent));
    ring.setAttribute("aria-label", progressAriaLabel(kind, caught, total, percent));
    const card = kind === "catalog" ? ring.closest(".fishydex-progress-overview") : ring.closest(".fishydex-grade-progress-card");
    const percentNode = ring.querySelector(kind === "catalog"
      ? ".fishydex-progress-pie-value"
      : ".fishydex-grade-progress-percent");
    if (percentNode) percentNode.textContent = `${percent}%`;
    const countNode = card?.querySelector(kind === "catalog"
      ? ".fishydex-progress-count"
      : ".fishydex-grade-progress-value");
    if (countNode) countNode.textContent = `${caught} / ${total}`;
    const remaining = card?.querySelector(kind === "catalog"
      ? ".fishydex-subtle"
      : ".fishydex-grade-progress-meta");
    if (remaining) {
      remaining.textContent = remainingLabel(
        total,
        caught,
        kind === "catalog" ? "fishydex.progress.empty" : "fishydex.progress.empty_grade",
      );
    }
  }

  render() {
    const state = this.#state;
    if (state === null) return;
    this.setAttribute("aria-busy", String(state._loading));
    this.#renderProgress("catalog");
    for (const grade of GRADE_ORDER) this.#renderProgress(grade);
    setVisible(this.querySelector("[data-fishydex-grade-stats]"), state.supports_grade_filter);
    const search = this.querySelector(".fishydex-search");
    if (search instanceof HTMLInputElement && document.activeElement !== search && search.value !== state.search_query) {
      search.value = state.search_query;
    }
    const nonFish = this.querySelector("[data-fishydex-show-non-fish]");
    if (nonFish instanceof HTMLInputElement) nonFish.checked = state.show_non_fish;
    const dried = this.querySelector("[data-fishydex-show-dried]");
    setVisible(dried?.closest("label"), state.supports_dried_filter);
    if (dried instanceof HTMLInputElement) {
      dried.checked = state.show_dried;
      dried.disabled = state._show_dried_pending;
    }
    const matches = this.querySelector(".fishydex-filter-count");
    const grid = this.querySelector("#fishydex-grid");
    const authoredResultsVisible = !state._query_unavailable
      && grid?.dataset.fishydexAuthoredCatalog === "true";
    const catalogPending = state._catalog_state === "pending" && !authoredResultsVisible;
    setVisible(this.querySelector("[data-fishydex-catalog-loading]"), catalogPending);
    setVisible(grid, !state._query_unavailable && !catalogPending);
    const matchesPending = state._loading || state._query_unavailable;
    if (matches) {
      matches.setAttribute("aria-busy", String(matchesPending));
      const value = matches.querySelector(".fishydex-filter-count-value");
      if (value) value.textContent = matchesPending ? "" : matchesLabel(state.visible_count, state.catalog_count);
      setVisible(value, !matchesPending);
      setVisible(matches.querySelector(".fishydex-filter-count-placeholder"), matchesPending);
    }
    const error = this.querySelector(".fishydex-error");
    setVisible(error, Boolean(state._api_error_message));
    const errorTitle = error?.querySelector("strong");
    if (errorTitle) errorTitle.textContent = state._api_error_message;
    const errorHint = error?.querySelector(".fishydex-subtle");
    setVisible(errorHint, Boolean(state._api_error_hint));
    if (errorHint) errorHint.textContent = state._api_error_hint;
    for (const button of this.querySelectorAll("[data-fishydex-catalog-view]")) {
      const guide = button.dataset.fishydexCatalogView === "guide";
      setVisible(button, !guide || state.supports_guide_view);
      setPressed(button, button.dataset.fishydexCatalogView === state.catalog_view, "tab-active");
    }
    const sortControls = this.querySelector("[data-fishydex-sort-controls]");
    if (sortControls instanceof HTMLElement) {
      const inactive = state.catalog_view === "guide";
      sortControls.inert = inactive;
      sortControls.setAttribute("aria-hidden", String(inactive));
    }
    for (const button of this.querySelectorAll("[data-fishydex-sort]")) {
      const field = button.dataset.fishydexSort;
      setPressed(button, field === state.sort_field, "is-active");
      button.setAttribute("aria-label", sortLabel(
        field,
        state.sort_field,
        state.sort_direction,
      ));
      for (const ascending of button.querySelectorAll("[data-fishydex-sort-direction='asc']")) {
        setVisible(ascending, field !== state.sort_field || state.sort_direction === "asc");
      }
      for (const descending of button.querySelectorAll("[data-fishydex-sort-direction='desc']")) {
        setVisible(descending, field === state.sort_field && state.sort_direction === "desc");
      }
    }
  }
}

if (!customElements.get("fishydex-surface")) {
  customElements.define("fishydex-surface", FishydexSurfaceElement);
}
